import { buddyClient, qs } from './client';

/**
 * Agentic monitors (H4): buddy3's `/admin/monitors/*`. Types follow buddy3's `packages/contracts/src/monitoring.ts`
 * (`MonitorEndpoints.View`: camelCase, nulls omitted). Monitor, run and alert ids are GUIDs, system ids slugs.
 */

export type MonitorScope = 'global' | 'tenant' | 'system';
export type MonitorSourceType = 'query' | 'gcp_logs' | 'llm';
export type MonitorNotifyMode = 'off' | 'new_alerts' | 'findings';

export interface MonitorSource {
  id?: string;
  ordinal?: number;
  /** `^[a-z0-9_-]+$`, unique within the monitor. */
  label: string;
  type: MonitorSourceType | (string & {});
  /** Literal query (`query`), Cloud Logging filter condition (`gcp_logs`), or `""` (`llm`). */
  query: string;
  /** `gcp_logs` only: lookback (`Nm|Nh|Nd`) overriding the monitor's `logSince`. */
  logSince?: string | null;
  /** Columns (query) or dot-paths (gcp_logs) that identify an at-risk item. */
  entityKeyColumns?: string[] | null;
  /** `llm` only: what to check and what counts as at-risk. */
  instructions?: string | null;
}

/** Report-mode email layout; edited as JSON. */
export interface ReportSpec {
  title?: string;
  rows: { type: string; [key: string]: unknown }[];
  [key: string]: unknown;
}

export interface Monitor {
  id: string;
  tenantId?: string | null;
  systemId?: string | null;
  scope: MonitorScope;
  name: string;
  description?: string | null;
  /** 5-field UTC cron. */
  cronExpression: string;
  /** Human-readable schedule from buddy3. */
  schedule?: string;
  instructions: string;
  maxToolCalls: number;
  enabled: boolean;
  combineMode: string;
  gateInstructions?: string | null;
  notifyEmails: string[];
  notifyMode: string;
  runMode: string;
  reportSpec?: ReportSpec | null;
  logSince?: string | null;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
  sources: MonitorSource[];
  /** Scheduled cards and agents made from a chat answer: the owner and the saved response they replay (no sources). */
  ownerUserCode?: string | null;
  savedQueryId?: string | null;
  fromResponse?: boolean;
  resultFrame?: number | null;
  sinceLastRun?: boolean;
  /** Whose rights a run has (nyceBuddy ADR-0022): `owner` = a token minted from the owner's sign-in; read-only here. */
  runAs?: 'owner' | 'service';
}

/** Create/update body; on update every absent field is kept. */
export interface MonitorInput {
  tenantId?: string | null;
  systemId?: string | null;
  name?: string;
  description?: string | null;
  cronExpression?: string;
  instructions?: string;
  maxToolCalls?: number | null;
  enabled?: boolean;
  combineMode?: string | null;
  gateInstructions?: string | null;
  notifyEmails?: string[];
  notifyMode?: string | null;
  runMode?: string | null;
  reportSpec?: ReportSpec | null;
  logSince?: string | null;
  sources?: MonitorSource[];
}

export interface MonitorList {
  items: Monitor[];
  scope: string;
}

export interface MonitorAssignment {
  id: string;
  monitorId: string;
  systemId: string;
  enabled: boolean;
  cronOverride?: string | null;
  notifyEmails?: string[] | null;
  notifyMode?: string | null;
  nextRunAt?: string | null;
  lastRunAt?: string | null;
}

/** Absent = keep, empty string/list = clear (inherit from the monitor). */
export interface MonitorAssignmentInput {
  enabled?: boolean;
  cronOverride?: string | null;
  notifyEmails?: string[] | null;
  notifyMode?: string | null;
}

export interface MonitorDetail {
  monitor: Monitor;
  assignments: MonitorAssignment[];
  canManage: boolean;
}

export interface MonitorProposedAction {
  /** `manual`, or `email_report` (the only executable one). */
  type: string;
  description: string;
}

export interface MonitorProposalContent {
  type: string;
  text?: string;
  name?: string;
  columns?: string[];
  data?: Record<string, unknown>[];
}

export interface MonitorProposalItem {
  entityKey: string;
  title: string;
  severity: string;
  finding: string;
  proposedActions: MonitorProposedAction[];
  content?: MonitorProposalContent[];
}

export interface MonitorProposal {
  severity: string;
  summary: string;
  items?: MonitorProposalItem[];
}

export interface MonitorRun {
  id: string;
  monitorId: string;
  tenantId: string;
  systemId: string;
  triggerSource: string;
  /** running | success | failed | no_rows | skipped */
  status: string;
  startedAt: string;
  finishedAt?: string | null;
  triggerRowCount?: number | null;
  newAlertCount?: number | null;
  stillOpenCount?: number | null;
  autoClosedCount?: number | null;
  snapshotTruncated?: boolean;
  triggerSnapshot?: unknown;
  proposal?: MonitorProposal | null;
  error?: string | null;
  model?: string | null;
  totalTokens?: number;
  estimatedCost?: number;
  toolCallCount?: number;
}

export interface MonitorAlert {
  id: string;
  monitorId: string;
  tenantId: string;
  systemId: string;
  entityKey: string;
  /** open | resolved */
  status: string;
  rowSnapshot?: unknown;
  /** A full run proposal or the one item of this alert. */
  proposal?: MonitorProposal | MonitorProposalItem | null;
  openedAt: string;
  lastSeenAt?: string | null;
  resolvedAt?: string | null;
}

export interface MonitorAlertQuery {
  monitorId?: string;
  systemId?: string;
  /** `open` (buddy3's default), `resolved`; absent with a monitor = all. */
  status?: string;
  limit?: number;
}

export interface MonitorActionLogEntry {
  id: string;
  alertId: string;
  actionIndex: number;
  actionType: string;
  description?: string | null;
  executedBy: string;
  executedAt: string;
  success: boolean;
  result?: string | null;
}

export interface MonitorActionResult {
  success: boolean;
  detail?: string | null;
  error?: string | null;
}

/** One email attempt of the monitor (buddy3's outbox). */
export interface MonitorNotification {
  id: string;
  createdAt: string;
  runId?: string | null;
  systemId: string;
  recipients: string[];
  subject: string;
  htmlBody?: string;
  textBody?: string | null;
  /** sent | mocked | failed */
  status: string;
}

export interface MonitorSourceTest {
  label: string;
  type: string;
  success: boolean;
  error?: string | null;
  rowCount: number;
  columns: string[];
  truncated?: boolean;
  /** At most 50 rows. */
  rows: Record<string, unknown>[];
  warnings: string[];
}

export interface MonitorTestResult {
  systemId: string;
  sources: MonitorSourceTest[];
}

export interface MonitorDraft extends MonitorInput {
  assumptions?: string[];
}

export interface MonitorGenerateResponse {
  draft: MonitorDraft;
  warnings: string[];
  validation: Omit<MonitorSourceTest, 'rows' | 'truncated'>[];
  attempts?: number;
  calls?: number;
  model?: string;
}

/** The lookups the monitor pages need from buddy3's `/systems` and `/tenants`. */
export interface BuddySystemRef {
  id: string;
  name: string;
  tenantId: string;
}
export interface BuddyTenantRef {
  id: string;
  name: string;
}

const enc = encodeURIComponent;

/** One environment's monitor API. */
export const monitorsFor = (target: string) => {
  const { get, send } = buddyClient(target);
  const m = (id: string) => `/monitors/${enc(id)}`;
  return {
    list: (q: { scope?: string; scopeId?: string } = {}) => get<MonitorList>(`/monitors${qs(q)}`),
    get: (id: string) => get<MonitorDetail>(m(id)),
    create: (body: MonitorInput) => send<Monitor>('POST', '/monitors', body),
    update: (id: string, body: MonitorInput) => send<Monitor>('PUT', m(id), body),
    remove: (id: string) => send<{ deleted: boolean }>('DELETE', m(id)),
    generate: (body: { description: string; systemId?: string; tenantId?: string }) =>
      send<MonitorGenerateResponse>('POST', '/monitors/generate', body),
    /** The sources of an unsaved draft on one system: rows per source, no alerts. */
    testDraft: (draft: MonitorInput, systemId: string) =>
      send<MonitorTestResult>('POST', '/monitors/test-draft', { draft, systemId }),
    test: (id: string, systemId?: string) =>
      send<MonitorTestResult>('POST', `${m(id)}/test${qs({ systemId })}`),
    /** A real run now (alerts, emails); 409 while one is in progress. */
    run: (id: string, systemId?: string) => send<MonitorRun>('POST', `${m(id)}/run${qs({ systemId })}`),
    assign: (id: string, systemId: string, body: MonitorAssignmentInput) =>
      send<MonitorAssignment>('PUT', `${m(id)}/assignments/${enc(systemId)}`, body),
    unassign: (id: string, systemId: string) =>
      send<unknown>('DELETE', `${m(id)}/assignments/${enc(systemId)}`),
    runs: (id: string, q: { systemId?: string; limit?: number } = {}) =>
      get<MonitorRun[]>(`${m(id)}/runs${qs(q)}`),
    runDetail: (runId: string) => get<MonitorRun>(`/monitors/runs/${enc(runId)}`),
    alerts: ({ monitorId, ...q }: MonitorAlertQuery = {}) =>
      get<MonitorAlert[]>(monitorId ? `${m(monitorId)}/alerts${qs(q)}` : `/monitors/alerts${qs(q)}`),
    clearAlerts: (id: string, systemId?: string) =>
      send<{ resolved: number }>('POST', `${m(id)}/alerts/clear${qs({ systemId })}`),
    notifications: (id: string, limit = 100) =>
      get<MonitorNotification[]>(`${m(id)}/notifications${qs({ limit })}`),
    executeAction: (alertId: string, index: number) =>
      send<MonitorActionResult>('POST', `/monitors/alerts/${enc(alertId)}/actions/${index}/execute`),
    actionLog: (alertId: string) =>
      get<MonitorActionLogEntry[]>(`/monitors/alerts/${enc(alertId)}/action-log`),
    systems: () => get<BuddySystemRef[]>('/systems'),
    tenants: () => get<BuddyTenantRef[]>('/tenants'),
  };
};
